<template>
  <div class="opportunity-graph flex flex-col gap-4 border card rounded-md min-h-56 p-4">
    <div class="header flex flex-col sm:flex-row gap-4 items-start">
      <div class="flex-1 w-full">
        <h4 class="font-semibold">
          Opportunities
          <span class="text-slate-300 text-base capitalize">
            {{ chartDateRange.split('_').join(' ') }}
           </span>
        </h4>
        <div class="flex flex-wrap gap-2 mt-2">
          <span class="text-xs flex items-center"><span class="i-heroicons-minus-solid size-6 text-slate-300" />
            Sent
          </span>
          <span class="text-xs flex items-center"><span class="i-heroicons-minus-solid size-6 text-success" />
            Accepted
            <span class="percent-rate"> ({{ toFixed(acceptedPercent) || 0  }}%)</span>
          </span>
          <span class="text-xs flex items-center"><span class="i-heroicons-minus-solid size-6 text-warning" />
            Awaiting
            <span class="percent-rate"> ({{ toFixed(awaitingPercent) || 0  }}%)</span>
          </span>
        </div>
      </div>
      <RadioGroup v-model="chartDateRange" class="chart-filters bg-slate-100 border-border-slate-300 flex p-0.5 rounded-lg space-x-1 w-full sm:w-auto">
        <RadioGroupLabel class="sr-only">Date Range Filter</RadioGroupLabel>
        <RadioGroupOption v-slot="{ checked }" :key="dateRangeConstants.this_month" :value="dateRangeConstants.this_month" class="flex-1 sm:flex-none">
          <button
            :class="[
              'rounded-md font-semibold shadow-sm px-2 py-1 text-sm w-full',
              checked ? 'bg-white text-slate-950 ring-1 ring-inset ring-slate-300 hover:bg-slate-100' : ''
            ]"
          >
            Month
          </button>
        </RadioGroupOption>
        <RadioGroupOption v-slot="{ checked }" :key="dateRangeConstants.this_quarter" :value="dateRangeConstants.this_quarter" class="flex-1 sm:flex-none">
          <button
            :class="[
              'rounded-md font-semibold shadow-sm px-2 py-1 text-sm w-full',
              checked ? 'bg-white text-slate-950 ring-1 ring-inset ring-slate-300 hover:bg-slate-100' : ''
            ]"
          >
            Quarter
          </button>
        </RadioGroupOption>
        <RadioGroupOption v-slot="{ checked }" :key="dateRangeConstants.this_year" :value="dateRangeConstants.this_year" class="flex-1 sm:flex-none">
          <button
            :class="[
              'rounded-md font-semibold shadow-sm px-2 py-1 text-sm w-full',
              checked ? 'bg-white text-slate-950 ring-1 ring-inset ring-slate-300 hover:bg-slate-100' : ''
            ]"
          >
            Year
          </button>
        </RadioGroupOption>
      </RadioGroup>
    </div>
    <div class="chart min-h-56 w-full mx-auto">
      <div v-if="isChartDataEmpty" class="flex items-center justify-center h-56 text-slate-500">
        No data available for the selected period.
      </div>
      <div v-else class="bg-white">
        <div class="opportunity-rates flex flex-col items-baseline sm:flex-row gap-4 sm:gap-0">
          <div class="flex-1 justify-items-center text-center sm:text-left w-full sm:w-auto">
            <div class="flex flex-row sm:flex-col items-center sm:items-start gap-2 sm:gap-0">
              <h4 class="font-semibold text-slate-300">
                <span class="total"> {{ currency(totalSent) }}</span>
                <span class="total-count"> ({{ totalSentChartData.length }})</span>
              </h4>
              <span class="text-xs text-slate-500">Total Opportunity</span>
            </div>
          </div>
          <div class="flex-1 justify-items-center text-center sm:text-left w-full sm:w-auto">
            <div class="flex flex-row sm:flex-col items-center sm:items-start gap-2 sm:gap-0">
              <h4 class="font-semibold text-success">
                <span class="total-value"> {{ currency(totalAccepted) }}</span>
                <span class="total-count"> ({{ acceptedChartData.length }})</span>
              </h4>
              <span class="text-xs text-slate-500">Accepted</span>
            </div>
          </div>
          <div class="flex-1 justify-items-center text-center sm:text-left w-full sm:w-auto">
            <div class="flex flex-row sm:flex-col items-center sm:items-start gap-2 sm:gap-0">
              <h4 class="font-semibold text-warning">
                <span class="total-value"> {{ currency(totalAwaiting) }}</span>
                <span class="total-count"> ({{ awaitingChartData.length }})</span>
              </h4>
              <span class="text-xs text-slate-500">Awaiting Acceptance</span>
            </div>
          </div>
        </div>
        <VueApexCharts type="area" height="200px" :options="chartOptions" :series="chartSeries" />
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { computed, ref } from 'vue'
import {
  RadioGroup,
  RadioGroupLabel,
  RadioGroupOption,
} from '@headlessui/vue'
import { PROPOSAL_STATUS } from '@/models/proposal'
import ProposalService from '@/services/ProposalService'
import { useQuery } from '@tanstack/vue-query'
import VueApexCharts from 'vue3-apexcharts'
import { ApexOptions } from 'apexcharts'
import { currency, toFixed } from '@/utils'
import { dateRangeConstants, orderByDesc } from '@/utils/queryFilters'

const chartOptions: ApexOptions = {
  chart: {
    toolbar: {
      show: false
    },
  },
  legend: {
    show: false
  },
  xaxis: {
    type: 'datetime',
    labels: {
      format: 'MMM d, yyyy'
    }
  },
  yaxis: {
    labels: {
      formatter: (value: number) => currency(value),
    },
    forceNiceScale: true,
  },
  tooltip: {
    x: {
      show: true,
      format: 'MMM d, yyyy'
    },
    y: {
      formatter: (value: number) => currency(value)
    },
  },
  stroke: {
    curve: 'smooth',
    width: [2, 2, 2], // Apply to all series, or specify per series
    dashArray: [0, 0, 5] // Solid for Sent, Solid for Accepted, Dashed for Awaiting
  },
  colors: ['#CBD5E1', '#A3E635', '#F97316'], // gray, green, orange, '#F97316'(orange),
  dataLabels: {
    enabled: false
  },
  // grid: {
  //   row: {
  //     colors: ['#f3f4f5', 'transparent'],
  //     opacity: 0.5
  //   }
  // }
}

const chartDateRange = ref(dateRangeConstants.this_month)

function isDateInRange(date: Date, rangeType: string): boolean {
  const targetDate = new Date(date)
  targetDate.setHours(0, 0, 0, 0)

  const now = new Date()
  now.setHours(0, 0, 0, 0)

  let startDate: Date
  let endDate: Date

  switch (rangeType) {
    case dateRangeConstants.this_month:
      startDate = new Date(now.getFullYear(), now.getMonth(), 1)
      endDate = new Date(now.getFullYear(), now.getMonth() + 1, 0)
      break
    case dateRangeConstants.this_quarter:
      // Assuming quarter starts: Jan, Apr, Jul, Oct
      const currentMonth = now.getMonth()
      const quarterStartMonth = Math.floor(currentMonth / 3) * 3
      startDate = new Date(now.getFullYear(), quarterStartMonth, 1)
      endDate = new Date(now.getFullYear(), quarterStartMonth + 3, 0)
      break
    case dateRangeConstants.this_year:
      startDate = new Date(now.getFullYear(), 0, 1)
      endDate = new Date(now.getFullYear(), 11, 31)
      break
    default:
      return false // Or handle as an error/default case
  }
  return targetDate >= startDate && targetDate <= endDate
}

const { data: proposals } = useQuery({
  queryKey: ['proposal', 'opportunityGraph', chartDateRange],
  queryFn: async () => {
    const data = await new ProposalService().where(
      [],
      [orderByDesc('contractDate')]
    )

    return data
  }
})

const acceptedChartData = computed(() => {
  if (!proposals.value) return []
  return proposals.value
    .filter(p => p.status === PROPOSAL_STATUS.ACCEPTED && p.signature && p.signature.timestamp && isDateInRange(new Date(p.signature.timestamp), chartDateRange.value))
    .map(p => [new Date(p.signature.timestamp).getTime(), (p.contractTotal || 0)])
    .sort((a, b) => a[0] - b[0])
})
const awaitingChartData = computed(() => {
  if (!proposals.value) return []
  return proposals.value
    .filter(p => p.status === PROPOSAL_STATUS.AWAITING_ACCEPTANCE && isDateInRange(new Date(p.contractDate), chartDateRange.value))
    .map(p => [new Date(p.contractDate).getTime(), (p.contractTotal || 0)])
    .sort((a, b) => a[0] - b[0])
})
const totalSentChartData = computed(() => {
  if (!proposals.value) return []
  return proposals.value
    .filter(p => isDateInRange(new Date(p.contractDate), chartDateRange.value))
    .map(p => [new Date(p.contractDate).getTime(), (p.contractTotal || 0)])
    .sort((a, b) => a[0] - b[0])
})

const chartSeries = ref([
  {
    name: 'Proposals Sent',
    data: totalSentChartData
  },
  {
    name: 'Accepted',
    data: acceptedChartData
  },
  {
    name: 'Awaiting Acceptance',
    data: awaitingChartData
  }
])

const totalAccepted = computed(() => acceptedChartData.value.reduce((acc, [, total]) => acc + total, 0))
const totalAwaiting = computed(() => awaitingChartData.value.reduce((acc, [, total]) => acc + total, 0))
const totalSent = computed(() => totalSentChartData.value.reduce((acc, [, total]) => acc + total, 0))
// const totalNotAccepted = computed(() => totalSent.value  - totalAccepted.value)
const acceptedPercent = computed(() => totalAccepted.value / totalSent.value * 100)
const awaitingPercent = computed(() => totalAwaiting.value / totalSent.value * 100)
// const notAcceptedPercent = computed(() => totalNotAccepted.value / totalSent.value * 100)

const isChartDataEmpty = computed(() => {
  return !totalSentChartData.value.length && !acceptedChartData.value.length && !awaitingChartData.value.length
})
</script>